import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AddPropertyModel.css";

const API_BASE_URL = "/api";
// OR try without port if your backend serves both API and frontend:
// const API_BASE_URL = window.location.origin; // This will use the same origin as frontend

// ✅ FIX: Enhanced axios instance with CORS handling
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  // Add withCredentials if needed for cookies/auth
  // withCredentials: true,
});

// ✅ FIX: Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log(
      `🚀 Making ${config.method?.toUpperCase()} request to:`,
      config.url
    );
    console.log("Full config:", config);
    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// ✅ FIX: Add response interceptor
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response from ${response.config.url}:`, response.status);
    return response;
  },
  (error) => {
    console.error("❌ Response error:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

const AddPropertyModal = ({
  show,
  onHide,
  onSubmit,
  propertyOptions,
  isSubmitting,
}) => {
  const [checkingUser, setCheckingUser] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    // Existing fields
    name: "",
    price: "",
    address: "",
    beds: "",
    baths: "",
    size: "",
    type_id: "",
    location_id: "",
    area_id: "",
    available: true,
    rental_period: "",
    images: [],

    // New fields
    warehouseFacility: "", // Yes/No dropdown
    logisticFacility: "", // Yes/No dropdown
    highwayConnectivity: "", // Yes/No dropdown
    waterFacility: "", // Yes/No dropdown
    electricityFacility: "", // Yes/No dropdown
    wtpEtp: "", // WTP/ETP dropdown
    mpcbPermission: "", // Yes/No dropdown
    nearestLandmark: "", // Textbox
    featuredCategory: "", // Yes/No dropdown
  });

  const [previewImages, setPreviewImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [localSubmitting, setLocalSubmitting] = useState(false);

  // Dropdown options
  const yesNoOptions = [
    { value: "", label: "Select option" },
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  const readyToMoveOptions = [
    { value: "", label: "Select option" },
    { value: "Ready To Move", label: "Ready To Move" },
    { value: "No Ready To Move", label: "No Ready To Move" },
  ];

  const wtpEtpOptions = [
    { value: "", label: "Select option" },
    { value: "WTP", label: "WTP" },
    { value: "ETP", label: "ETP" },
    { value: "Both", label: "Both" },
    { value: "None", label: "None" },
  ];

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      setFormData({
        name: "",
        price: "",
        address: "",
        beds: "",
        baths: "",
        size: "",
        type_id: "",
        location_id: "",
        area_id: "",
        available: true,
        rental_period: "",
        images: [],
        warehouseFacility: "",
        logisticFacility: "",
        highwayConnectivity: "",
        waterFacility: "",
        electricityFacility: "",
        wtpEtp: "",
        mpcbPermission: "",
        nearestLandmark: "",
        featuredCategory: "",
      });
      setPreviewImages([]);
      setErrors({});
      setLocalSubmitting(false);
    }
  }, [show]);

  const checkUserExistence = async () => {
    setCheckingUser(true);
    const email = prompt("Please enter your email to continue:");

    if (email) {
      try {
        // ✅ CHANGED: Use relative URL instead of localhost URL
        const response = await api.get(`/check-user/${email}`);
        // OLD: `http://localhost:3000/check-user/${email}`

        if (response.data.exists) {
          if (response.data.isVerified) {
            navigate("/login", { state: { from: "add-property" } });
          } else {
            alert("Please verify your email first");
            navigate("/signup");
          }
        } else {
          navigate("/signup", { state: { email } });
        }
      } catch (error) {
        console.error("Error checking user:", error);
        alert("Error checking user existence");
      }
    }
    setCheckingUser(false);
    onHide();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);

    // Validate files
    const validFiles = files.filter((file) => {
      if (file.size > 1024 * 1024) {
        alert(`File ${file.name} is too large (max 1MB)`);
        return false;
      }
      if (!file.type.match("image/jpeg")) {
        alert(`File ${file.name} is not a JPEG image`);
        return false;
      }
      return true;
    });

    // Limit to 4 images
    const remainingSlots = 4 - formData.images.length;
    const filesToAdd = validFiles.slice(0, remainingSlots);

    if (filesToAdd.length < validFiles.length) {
      alert(`You can only upload up to 4 images total`);
    }

    setFormData({
      ...formData,
      images: [...formData.images, ...filesToAdd],
    });

    // Create previews
    const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
    setPreviewImages([...previewImages, ...newPreviews]);
  };

  const removeImage = (index) => {
    const newImages = [...formData.images];
    newImages.splice(index, 1);
    setFormData({ ...formData, images: newImages });

    const newPreviews = [...previewImages];
    URL.revokeObjectURL(newPreviews[index]); // Clean up memory
    newPreviews.splice(index, 1);
    setPreviewImages(newPreviews);
  };

  const validateForm = () => {
    const newErrors = {};

    // Existing validations
    if (!formData.name.trim()) newErrors.name = "Property name is required";
    if (!formData.price || isNaN(formData.price) || formData.price <= 0)
      newErrors.price = "Valid price is required";
    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.beds || isNaN(formData.beds) || formData.beds <= 0)
      newErrors.beds = "Valid number of beds is required";
    if (!formData.baths || isNaN(formData.baths) || formData.baths <= 0)
      newErrors.baths = "Valid number of baths is required";
    if (!formData.size || isNaN(formData.size) || formData.size <= 0)
      newErrors.size = "Valid size is required";
    if (!formData.type_id) newErrors.type_id = "Property type is required";
    if (!formData.location_id) newErrors.location_id = "Location is required";
    if (!formData.area_id) newErrors.area_id = "Area is required";

    // New field validations (optional fields, but validate if needed)
    if (
      formData.wtpEtp &&
      !["WTP", "ETP", "Both", "None"].includes(formData.wtpEtp)
    ) {
      newErrors.wtpEtp = "Please select a valid WTP/ETP option";
    }

    // Validate all dropdowns have valid values if filled
    if (
      formData.warehouseFacility &&
      !["Yes", "No"].includes(formData.warehouseFacility)
    ) {
      newErrors.warehouseFacility = "Please select Yes or No";
    }
    if (
      formData.logisticFacility &&
      !["Yes", "No"].includes(formData.logisticFacility)
    ) {
      newErrors.logisticFacility = "Please select Yes or No";
    }
    if (
      formData.highwayConnectivity &&
      !["Yes", "No"].includes(formData.highwayConnectivity)
    ) {
      newErrors.highwayConnectivity = "Please select Yes or No";
    }
    if (
      formData.waterFacility &&
      !["Yes", "No"].includes(formData.waterFacility)
    ) {
      newErrors.waterFacility = "Please select Yes or No";
    }
    if (
      formData.electricityFacility &&
      !["Yes", "No"].includes(formData.electricityFacility)
    ) {
      newErrors.electricityFacility = "Please select Yes or No";
    }
    if (
      formData.mpcbPermission &&
      !["Yes", "No"].includes(formData.mpcbPermission)
    ) {
      newErrors.mpcbPermission = "Please select Yes or No";
    }
    if (
      formData.featuredCategory &&
      !["Ready To Move", "No Ready To Move"].includes(formData.featuredCategory)
    ) {
      newErrors.featuredCategory =
        "Please select Ready To Move or No Ready To Move";
    }

    // Validate nearest landmark (optional, but validate length if provided)
    if (formData.nearestLandmark && formData.nearestLandmark.length > 200) {
      newErrors.nearestLandmark =
        "Nearest landmark must be less than 200 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting || localSubmitting) {
      console.log("Submission already in progress");
      return;
    }

    if (!validateForm()) return;

    setLocalSubmitting(true);

    try {
      // Prepare data for submission including all fields
      const submissionData = {
        ...formData,
        // Ensure proper formatting
        warehouseFacility: formData.warehouseFacility || null,
        logisticFacility: formData.logisticFacility || null,
        highwayConnectivity: formData.highwayConnectivity || null,
        waterFacility: formData.waterFacility || null,
        electricityFacility: formData.electricityFacility || null,
        wtpEtp: formData.wtpEtp || null,
        mpcbPermission: formData.mpcbPermission || null,
        nearestLandmark: formData.nearestLandmark || null,
        featuredCategory: formData.featuredCategory || null,
      };

      // Call parent's onSubmit function with form data
      await onSubmit(submissionData);

      // If onSubmit succeeds, close the modal
      onHide();
    } catch (error) {
      console.error("Error in modal submit handler:", error);
      // Error handling is done in the parent component
    } finally {
      setLocalSubmitting(false);
    }
  };

  if (checkingUser) {
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="loading-message">Checking user...</div>
        </div>
      </div>
    );
  }

  if (!show) return null;

  const isSubmitDisabled = isSubmitting || localSubmitting;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Add New Property</h2>
          <button
            className="close-button"
            onClick={onHide}
            disabled={isSubmitDisabled}
          >
            &times;
          </button>
        </div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            {/* Existing fields - Property Name and Price */}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="name">Property Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                />
                {errors.name && (
                  <span className="error-message">{errors.name}</span>
                )}
              </div>
              <div className="form-group half-width">
                <label htmlFor="price">Price *</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={errors.price ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                  min="0"
                  step="0.01"
                />
                {errors.price && (
                  <span className="error-message">{errors.price}</span>
                )}
              </div>
            </div>

            {/* Existing field - Address */}
            <div className="form-group">
              <label htmlFor="address">Address *</label>
              <textarea
                id="address"
                name="address"
                rows={2}
                value={formData.address}
                onChange={handleChange}
                className={errors.address ? "input-error" : ""}
                disabled={isSubmitDisabled}
              />
              {errors.address && (
                <span className="error-message">{errors.address}</span>
              )}
            </div>

            {/* Existing fields - Beds, Baths, Size (sqft) */}
            <div className="form-row">
              <div className="form-group third-width">
                <label htmlFor="beds">Beds *</label>
                <input
                  type="number"
                  id="beds"
                  name="beds"
                  value={formData.beds}
                  onChange={handleChange}
                  className={errors.beds ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                  min="0"
                />
                {errors.beds && (
                  <span className="error-message">{errors.beds}</span>
                )}
              </div>
              <div className="form-group third-width">
                <label htmlFor="baths">Baths *</label>
                <input
                  type="number"
                  id="baths"
                  name="baths"
                  value={formData.baths}
                  onChange={handleChange}
                  className={errors.baths ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                  min="0"
                />
                {errors.baths && (
                  <span className="error-message">{errors.baths}</span>
                )}
              </div>
              <div className="form-group third-width">
                <label htmlFor="size">Size (sqft) *</label>
                <input
                  type="number"
                  id="size"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  className={errors.size ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                  min="0"
                />
                {errors.size && (
                  <span className="error-message">{errors.size}</span>
                )}
              </div>
            </div>

            {/* Existing fields - Property Type, Location, Area */}
            <div className="form-row">
              <div className="form-group third-width">
                <label htmlFor="type_id">Property Type *</label>
                <select
                  id="type_id"
                  name="type_id"
                  value={formData.type_id}
                  onChange={handleChange}
                  className={errors.type_id ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  <option value="">Select type</option>
                  {propertyOptions.propertyTypes?.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.type} ({type.transactionType})
                    </option>
                  ))}
                </select>
                {errors.type_id && (
                  <span className="error-message">{errors.type_id}</span>
                )}
              </div>
              <div className="form-group third-width">
                <label htmlFor="location_id">Location *</label>
                <select
                  id="location_id"
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleChange}
                  className={errors.location_id ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  <option value="">Select location</option>
                  {propertyOptions.locations?.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
                {errors.location_id && (
                  <span className="error-message">{errors.location_id}</span>
                )}
              </div>
              <div className="form-group third-width">
                <label htmlFor="area_id">Area *</label>
                <select
                  id="area_id"
                  name="area_id"
                  value={formData.area_id}
                  onChange={handleChange}
                  className={errors.area_id ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  <option value="">Select area</option>
                  {propertyOptions.areas?.map((area) => (
                    <option key={area.id} value={area.id}>
                      {area.area}
                    </option>
                  ))}
                </select>
                {errors.area_id && (
                  <span className="error-message">{errors.area_id}</span>
                )}
              </div>
            </div>

            {/* NEW FIELDS SECTION - Facilities and Permissions */}
            <div className="section-divider">
              <h4>Facilities & Permissions</h4>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="warehouseFacility">Warehouse Facility</label>
                <select
                  id="warehouseFacility"
                  name="warehouseFacility"
                  value={formData.warehouseFacility}
                  onChange={handleChange}
                  className={errors.warehouseFacility ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.warehouseFacility && (
                  <span className="error-message">
                    {errors.warehouseFacility}
                  </span>
                )}
              </div>
              <div className="form-group half-width">
                <label htmlFor="logisticFacility">Logistic Facility</label>
                <select
                  id="logisticFacility"
                  name="logisticFacility"
                  value={formData.logisticFacility}
                  onChange={handleChange}
                  className={errors.logisticFacility ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.logisticFacility && (
                  <span className="error-message">
                    {errors.logisticFacility}
                  </span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="highwayConnectivity">
                  Highway Connectivity
                </label>
                <select
                  id="highwayConnectivity"
                  name="highwayConnectivity"
                  value={formData.highwayConnectivity}
                  onChange={handleChange}
                  className={errors.highwayConnectivity ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.highwayConnectivity && (
                  <span className="error-message">
                    {errors.highwayConnectivity}
                  </span>
                )}
              </div>
              <div className="form-group half-width">
                <label htmlFor="waterFacility">Water Facility</label>
                <select
                  id="waterFacility"
                  name="waterFacility"
                  value={formData.waterFacility}
                  onChange={handleChange}
                  className={errors.waterFacility ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.waterFacility && (
                  <span className="error-message">{errors.waterFacility}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="electricityFacility">
                  Electricity Facility
                </label>
                <select
                  id="electricityFacility"
                  name="electricityFacility"
                  value={formData.electricityFacility}
                  onChange={handleChange}
                  className={errors.electricityFacility ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.electricityFacility && (
                  <span className="error-message">
                    {errors.electricityFacility}
                  </span>
                )}
              </div>
              <div className="form-group half-width">
                <label htmlFor="wtpEtp">WTP or ETP</label>
                <select
                  id="wtpEtp"
                  name="wtpEtp"
                  value={formData.wtpEtp}
                  onChange={handleChange}
                  className={errors.wtpEtp ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {wtpEtpOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.wtpEtp && (
                  <span className="error-message">{errors.wtpEtp}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="mpcbPermission">MPCB Govt Permission</label>
                <select
                  id="mpcbPermission"
                  name="mpcbPermission"
                  value={formData.mpcbPermission}
                  onChange={handleChange}
                  className={errors.mpcbPermission ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {yesNoOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.mpcbPermission && (
                  <span className="error-message">{errors.mpcbPermission}</span>
                )}
              </div>
              <div className="form-group half-width">
                <label htmlFor="featuredCategory">Featured Category</label>
                <select
                  id="featuredCategory"
                  name="featuredCategory"
                  value={formData.featuredCategory}
                  onChange={handleChange}
                  className={errors.featuredCategory ? "input-error" : ""}
                  disabled={isSubmitDisabled}
                >
                  {readyToMoveOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.featuredCategory && (
                  <span className="error-message">
                    {errors.featuredCategory}
                  </span>
                )}
              </div>
            </div>

            {/* New field - Nearest Landmark */}
            <div className="form-group">
              <label htmlFor="nearestLandmark">Nearest Landmark</label>
              <input
                type="text"
                id="nearestLandmark"
                name="nearestLandmark"
                value={formData.nearestLandmark}
                onChange={handleChange}
                className={errors.nearestLandmark ? "input-error" : ""}
                disabled={isSubmitDisabled}
                placeholder="e.g., Near Shopping Mall, Opposite Metro Station"
                maxLength="200"
              />
              <div className="character-count">
                {formData.nearestLandmark.length}/200 characters
              </div>
              {errors.nearestLandmark && (
                <span className="error-message">{errors.nearestLandmark}</span>
              )}
            </div>

            {/* Existing fields - Available and Rental Period */}
            <div className="form-row">
              <div className="form-group half-width">
                <label className="checkbox-container">
                  <input
                    type="checkbox"
                    id="available"
                    name="available"
                    checked={formData.available}
                    onChange={handleChange}
                    disabled={isSubmitDisabled}
                  />
                  <span className="checkmark"></span>
                  Available
                </label>
              </div>
              <div className="form-group half-width">
                <label htmlFor="rental_period">
                  Rental Period (if applicable)
                </label>
                <input
                  type="text"
                  id="rental_period"
                  name="rental_period"
                  value={formData.rental_period}
                  onChange={handleChange}
                  disabled={isSubmitDisabled}
                  placeholder="e.g., 12 months"
                />
              </div>
            </div>

            {/* Existing field - Images */}
            <div className="form-group">
              <label htmlFor="images">
                Property Images (max 4, JPEG only, 1MB max each)
              </label>
              <input
                type="file"
                id="images"
                name="images"
                multiple
                accept="image/jpeg"
                onChange={handleImageChange}
                disabled={formData.images.length >= 4 || isSubmitDisabled}
              />
              <div className="file-info">
                {formData.images.length} / 4 images selected
              </div>
            </div>

            {previewImages.length > 0 && (
              <div className="image-previews">
                <h6>Image Previews:</h6>
                <div className="preview-container">
                  {previewImages.map((preview, index) => (
                    <div key={index} className="preview-item">
                      <img
                        src={preview}
                        alt={`Preview ${index}`}
                        className="thumbnail"
                      />
                      <button
                        className="remove-image-button"
                        onClick={() => removeImage(index)}
                        disabled={isSubmitDisabled}
                        type="button"
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="cancel-button"
              onClick={onHide}
              disabled={isSubmitDisabled}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={isSubmitDisabled}
            >
              {isSubmitDisabled ? "Submitting..." : "Submit Property"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPropertyModal;
